import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import Layout from '../../../components/layout';
import { Notification } from '../../../components/notifications';
import { createNotification, getNotificationGroupTypes } from '../../../api/services/notificationService';
import { FieldInput } from '../../../components/fieldInput';
import SubmitButton from '../../../components/submitButton';
import NotificationRequest from '../../../api/requests/notificationRequest';
import NotificationGroupType from '../../../api/models/notificationGroupType';
import NotificationGroup from '../../../api/models/notificationGroup';
import SelectMulti, { MultiValue } from 'react-select';
import { getDisciplines } from '../../../api/services/disciplineService';
import { selectOption } from '../../../types/selectOption';
import { generateSelectOptions } from '../../../common/utils';
import { getDistrictOptions } from '../../../api/services/districtService';
import { getDivisionOptions } from '../../../api/services/divisionService';
import { getRoles } from '../../../api/services/roleService';
import { NotificationGroupTypeIdLookup } from '../../../common/constants';
import { getSchoolsByDistricts } from '../../../api/services/schoolService';
import { useHistory } from 'react-router-dom';
import DOMPurify from 'dompurify';

//Import and configure the editor
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
const editorAcceptedFormats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link', 'color'];
const editorAcceptedModules = { toolbar: [['bold', 'italic', 'underline', 'strike'], [{ 'header': 1 }, { 'header': 2 }], [{ 'list': 'ordered' }, { 'list': 'bullet' }], [{ 'header': [1, 2, 3, 4, 5, 6, false] }], [{ 'color': [] }], ['clean']] };

const NewNotificationPage = () => {
  const history = useHistory();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [message, setMessage] = useState('');
  const [groupTypes, setGroupTypes] = useState<NotificationGroupType[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<NotificationGroup[]>([]);

  //Options
  const [districtOptions, setDistrictOptions] = useState<selectOption[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<selectOption[]>([]);
  const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<selectOption[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<selectOption[]>([]);

  //Selected options
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<number[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<number[]>([]);

  const [saveError, setSaveError] = useState('');

  //Sanitize title and text
  const sanitizeInput = (input: string) => {
    return DOMPurify.sanitize(input);
  };

  //Data Fetching
  const districtsData = useAsync({
    promiseFn: getDistrictOptions,
    onResolve: (u) => setDistrictOptions(generateSelectOptions(u.data, 'value', 'label')),
  });

  const divisionsData = useAsync({
    promiseFn: getDivisionOptions,
    onResolve: (u) => setDivisionOptions(generateSelectOptions(u.data, 'value', 'label')),
  });

  const disciplinesData = useAsync({
    promiseFn: getDisciplines,
    onResolve: (u) => setDisciplineOptions(generateSelectOptions(u.data.disciplines, 'disciplineId', 'name', true)),
  });

  const rolesData = useAsync({
    promiseFn: getRoles,
    onResolve: (u) => setRoleOptions(generateSelectOptions(u.data.roles, 'roleId', 'name')),
  });

  const groupTypesData = useAsync({
    promiseFn: getNotificationGroupTypes, onResolve: (r) => {
      setGroupTypes(r.data);
    }
  });

  /**
     * Whenever district changes, update the list of schools
     */
  useEffect(() => {
    if (selectedDistricts.length > 0) updateSchools(selectedDistricts);
    if (selectedDistricts.length === 0) setSchoolOptions([{ value: -1, label: '' }]);
  }, [selectedDistricts]);

  //Save Notification
  const saveNotification = async () => {
    const request: NotificationRequest = {
      title: sanitizeInput(title),
      text: sanitizeInput(text),
      // title,
      // text,
      isVisible
    }

    //Generate the groups
    request.groups = [];
    selectedDistricts.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.District, actionId, isVisible }));
    selectedDivisions.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Division, actionId, isVisible }));
    selectedDisciplines.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Discipline, actionId, isVisible }));
    selectedRoles.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.Role, actionId, isVisible }));
    selectedSchools.forEach(actionId => request.groups?.push({ notificationGroupTypeId: NotificationGroupTypeIdLookup.School, actionId, isVisible }));

    return await createNotification(request);
  }

  const updateSchools = async (districts: number[]) => {
    const response = await getSchoolsByDistricts(districts);

    //If we don't have any schools, inform the user
    if (response.data.schools.length === 0) {
      setSchoolOptions([{ value: -1, label: '' }]);
      return true;
    }

    setSchoolOptions(generateSelectOptions(response.data.schools, 'schoolId', 'name', true));
  }

  const save = useAsync({
    deferFn: saveNotification,
    onResolve: (r) => {
      history.push('/admin/notifications', {
        message: r.status >= 200 && r.status < 400 ?
          'The notification has been saved successfully' :
          'There was an error saving the notification'
      });
    }
  });

  const handleGroupTypeSelection = (id: number) => {
    if (selectedGroups.filter(s => s.notificationGroupTypeId === id).length > 0) {
      setSelectedGroups(selectedGroups.filter(s => s.notificationGroupTypeId !== id));
    } else {
      setSelectedGroups([...selectedGroups, { notificationGroupTypeId: id }]);
    }
  }

  const handleGroupSelection = (options: MultiValue<selectOption>, groupTypeId: number) => {
    const ids: number[] = [];
    options.map(x => ids.push(x.value!))
    switch (groupTypeId) {
      case NotificationGroupTypeIdLookup.District:
        setSelectedDistricts(ids);
        break;
      case NotificationGroupTypeIdLookup.Division:
        setSelectedDivisions(ids);
        break;
      case NotificationGroupTypeIdLookup.Discipline:
        setSelectedDisciplines(ids);
        break;
      case NotificationGroupTypeIdLookup.Role:
        setSelectedRoles(ids);
        break;
      case NotificationGroupTypeIdLookup.School:
        setSelectedSchools(ids);
        break;
    }
  }

  return (
    <Layout isLoading={
      groupTypesData.isPending ||
      districtsData.isPending ||
      divisionsData.isPending ||
      disciplinesData.isPending ||
      rolesData.isPending ||
      save.isPending
    }>
      <Fragment>
        {message && <Notification isSuccess>{message}</Notification>}
        {saveError && <Notification isSuccess={false}>{saveError}</Notification>}

        <div className="grid grid-cols-2">
          <div className='col-span-2 mt-4 mb-4 ml-3 text-sm'>
            <input
              type='checkbox'
              className='primary-checkbox'
              checked={isVisible}
              onChange={(e) => setIsVisible(e.currentTarget.checked)}
            />
            <label htmlFor='add-contact-is-invoice' className='px-2 primary'>Is Visible</label>
          </div>
          <div className="col-span-2 mb-4 ml-3 text-sm">
            <FieldInput
              label='Title'
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              minLength={1}
            />
          </div>
          <div className="col-span-2 mb-4 ml-3 text-sm">
            <label>Content</label>
            <ReactQuill
              theme="snow"
              value={text}
              onChange={setText}
              formats={editorAcceptedFormats}
              modules={editorAcceptedModules}
            />
          </div>
          <div className='col-span-2 mb-2 ml-3'>
            <label>Notification Groups</label>
            <div className={`px-4 rounded-lg w-12/12 pt-2 mb-4 pb-4 bg-white shadow`}>
              {groupTypes.map((group) => {
                let options: selectOption[] = [];
                let selectedOptions: selectOption[] = [];

                switch (group.notificationGroupTypeId) {
                  case NotificationGroupTypeIdLookup.District:
                    options = districtOptions;
                    break;
                  case NotificationGroupTypeIdLookup.Division:
                    options = divisionOptions;
                    break;
                  case NotificationGroupTypeIdLookup.Role:
                    options = roleOptions;
                    break;
                  case NotificationGroupTypeIdLookup.School:
                    options = schoolOptions;
                    break;
                  case NotificationGroupTypeIdLookup.Discipline:
                    options = disciplineOptions;
                    break;
                }

                return <Fragment key={`notification-group-container-${group.notificationGroupTypeId}`}>
                  <div className='flex items-start py-1'>
                    <div className='flex items-center h-5'>
                      <input
                        id={`notification-group-${group.notificationGroupTypeId}`}
                        value={group.notificationGroupTypeId}
                        checked={selectedGroups.filter(s => s.notificationGroupTypeId === group.notificationGroupTypeId).length > 0}
                        onChange={(e) => handleGroupTypeSelection(Number(e.target.value))}
                        name='entry-type'
                        type='checkbox'
                        className={`bg-white`}
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor={`notification-group-${group.notificationGroupTypeId}`}>{group.name}</label>
                    </div>
                  </div>
                  <div className="group-options">
                    <SelectMulti
                      className={`${selectedGroups.filter((g) => g.notificationGroupTypeId == group.notificationGroupTypeId).length > 0 ? "" : "hidden"}`}
                      isMulti={true}
                      options={options}
                      //defaultValue={selectedGroups.filter((g) => g.notificationGroupTypeId == group.notificationGroupTypeId).length > 0 ? selectedGroups.filter((g) => g.notificationGroupTypeId == group.notificationGroupTypeId) ?? [] : []}
                      onChange={(e) => handleGroupSelection(e, group.notificationGroupTypeId!)}
                    />
                  </div>
                </Fragment>
              })}
            </div>
          </div>
          <div className='col-span-2 mb-4 ml-3 text-sm'>
            <SubmitButton
              text='Save Notification'
              disabled={(!text || text.trim() === '')}
              onClick={() => save.run()}
              className='float-right'
            />
          </div>
        </div>
      </Fragment>
    </Layout>
  );
};

export default NewNotificationPage;